import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { msalInstance } from './msal';
import { goToLoginInteractive, markInteractionRequired } from './interaction-required';
import { getCurrentUserType } from './user-type';
import { getAuth0Token, clearAuth0Session, logoutAuth0, isAuth0Configured } from './auth0';
import { getTracker } from '@/telemetry/tracker';

/**
 * Unified USER_CONTEXT sessionStorage key (`legacy-user-bridge.ts`
 * `USER_CONTEXT_KEY`). The literal is duplicated here — rather than imported —
 * to avoid an `auth-module ⇄ auth-provider ⇄ client-store` import cycle.
 */
const USER_CONTEXT_KEY = 'ey.user.context';
/** localStorage flag persisted by the Auth0 callback (see auth-provider.ts). */
const AUTH_PROVIDER_KEY = 'authProvider';

/**
 * Whether the SHELL SESSION is owned by Auth0 (independent mode). Duplicates
 * the `getShellAuthProvider()` logic (env `VITE_AUTH_PROVIDER` + Auth0 config)
 * rather than importing `auth-provider` here, to avoid the
 * `auth-module ⇄ auth-provider ⇄ client-store` import cycle.
 */
function shellIsAuth0(): boolean {
  return (
    (import.meta.env.VITE_AUTH_PROVIDER ?? '').trim().toLowerCase() === 'auth0' &&
    isAuth0Configured()
  );
}

/**
 * D9 — when the active client changes the client-store calls
 * `markTokenRotationOnNextCall()`. The next `getTokenAsync()` then sets
 * `forceRefresh: true` on `acquireTokenSilent`, guaranteeing the MFE receives
 * a token bound to the new client context (legacy parity:
 * `currentlySelectedClientId` is written **before** any token call).
 */
let forceRefreshNext = false;
// D9 (Auth0 parity): on a client switch the next Auth0 token acquisition must
// also rebind `paramclientid`, so force a fresh (`cacheMode: 'off'`) Auth0
// token on the next `getTokenAsync({ provider: 'auth0' })` call.
let auth0ForceRefreshNext = false;
export function markTokenRotationOnNextCall(): void {
  forceRefreshNext = true;
  auth0ForceRefreshNext = true;
  // The client context changed — every cached access token is now stale.
  clearTokenCache();
  // Notify other shell caches keyed by client (e.g. the legacy RUM-token
  // cache in install-globals) so they don't serve a token for the old client.
  for (const cb of rotationListeners) {
    try {
      cb();
    } catch {
      /* a listener throwing must not block token rotation */
    }
  }
}

/**
 * Register a callback fired whenever the active client rotates (i.e. on
 * `markTokenRotationOnNextCall`). Used by the legacy RUM-token cache to
 * invalidate per-client entries. Returns an unsubscribe fn.
 */
const rotationListeners = new Set<() => void>();
export function onTokenRotation(cb: () => void): () => void {
  rotationListeners.add(cb);
  return () => rotationListeners.delete(cb);
}

/**
 * Register a callback fired on `logout()`. Used to clear per-session caches
 * (e.g. the legacy RUM-token cache) so nothing leaks into the next session.
 * Legacy clears the RUM cache on session events; we mirror that on logout.
 * Returns an unsubscribe fn.
 */
const logoutListeners = new Set<() => void>();
export function onLogout(cb: () => void): () => void {
  logoutListeners.add(cb);
  return () => logoutListeners.delete(cb);
}

// ---- In-memory access-token cache + single-flight -----------------------
//
// WHY: `http()` (shell) and `EY_RAPToR_getAccessTokenforMFE` (every legacy MFE
// request) both call `getTokenAsync()` before each call. Hitting
// `acquireTokenSilent` per request is expensive — MSAL serialises behind a
// global interaction lock, so a burst of parallel MFE requests queues up and
// stalls (the "requests are slow" symptom). MSAL caches internally, but the
// lock + validation overhead is paid every call.
//
// This adds a thin layer:
//   - cache: per (account, scopes) access token + absolute expiry. Returned
//     directly while still comfortably valid (EXPIRY_SKEW_MS before expiry).
//   - single-flight: concurrent misses for the same key share ONE
//     `acquireTokenSilent` promise instead of stampeding the MSAL lock.
//
// Invalidated on client rotation (forceRefresh) and on logout. Keyed by
// `homeAccountId` so a re-login as a different user can never reuse a token.
interface CachedToken {
  token: string;
  expiresAt: number;
}
const tokenCache = new Map<string, CachedToken>();
const inflight = new Map<string, Promise<string>>();
/** Refresh this long before the real expiry so a token is never served stale. */
const EXPIRY_SKEW_MS = 2 * 60_000;

export function clearTokenCache(): void {
  tokenCache.clear();
  inflight.clear();
}

/**
 * Clears localStorage and sessionStorage on logout, then sets the
 * interaction-required flag so the login page shows a sign-in button
 * instead of auto-redirecting.
 *
 * Mirrors Angular `login.service.ts:loggedout()`:
 *   localStorage.clear(); sessionStorage.clear();
 *   sessionStorage.setItem('ey.interaction.needed', 'true');
 *
 * This removes MSAL's cached accounts from storage so the next visit
 * does not auto-login.
 */
export function clearAllStorage(): void {
  try {
    localStorage.clear();
  } catch {
    /* storage unavailable */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* storage unavailable */
  }
  markInteractionRequired();
}

function cacheKey(homeAccountId: string, scopes: string[]): string {
  return `${homeAccountId}|${[...scopes].sort().join(' ')}`;
}

function storeResult(key: string, result: AuthenticationResult): void {
  const expiresAt = result.expiresOn ? result.expiresOn.getTime() : Date.now() + 5 * 60_000;
  tokenCache.set(key, { token: result.accessToken, expiresAt });
}

/**
 * Minimal projection of MSAL `AccountInfo` onto the SDK `User` shape.
 *
 * The SDK's `User` interface (see `shell-ui-sdk/src/types/index.ts`) requires
 * fields like `oid`, `principalName`, `issuer`, `type`. We map best-effort and
 * default the rest. Exposed as `unknown`-typed object via `_internal.register`
 * — full parity is a M3 task.
 */
export function toUser(account: AccountInfo | null) {
  if (!account) return null;
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  return {
    email: account.username,
    name: account.name ?? account.username,
    type: getCurrentUserType(),
    oid: account.localAccountId ?? account.homeAccountId,
    principalName: account.username,
    issuer: typeof claims.iss === 'string' ? claims.iss : '',
  };
}

/**
 * Project the unified `USER_CONTEXT` (`ey.user.context`) onto the SDK `User`
 * shape. Populated by the Auth0 callback for Auth0 sessions (and by
 * `legacy-user-bridge` for MSAL); reading it keeps `EY_RAPToR_getUserOID` /
 * `getUserEmail` / `Username` correct regardless of which provider signed the
 * user in (PRTL-2857 1a). Returns `null` when the key is absent/unparseable.
 */
function readUserContextUser() {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(USER_CONTEXT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: {
    username?: string;
    name?: string;
    localAccountId?: string;
    idTokenClaims?: Record<string, unknown>;
  };
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const claims = (parsed.idTokenClaims ?? {}) as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const email = str(claims.email) || str(claims.preferred_username) || str(parsed.username);
  return {
    email,
    name: str(parsed.name) || str(claims.name) || email,
    type: getCurrentUserType(),
    oid: str(claims.oid) || str(parsed.localAccountId),
    principalName: str(claims.preferred_username) || str(parsed.username) || email,
    issuer: str(claims.iss),
  };
}

export const authModule = {
  get user() {
    // Auth0-active sessions resolve identity from the unified USER_CONTEXT the
    // Auth0 callback wrote; MSAL sessions fall through to the active account.
    try {
      if (localStorage.getItem(AUTH_PROVIDER_KEY) === 'auth0') {
        const ctx = readUserContextUser();
        if (ctx) return ctx;
      }
    } catch {
      /* storage unavailable — fall through to MSAL */
    }
    return toUser(msalInstance.getActiveAccount());
  },

  async getTokenAsync(
    scopes: string[] = [],
    opts: { interactive?: boolean; provider?: 'msal' | 'auth0'; paramclientid?: string } = {},
  ): Promise<string> {
    // Auth0 branch (dual-provider). Two callers pass `provider: 'auth0'`:
    //   • the per-MFE token path, which resolves `paramclientid` at its own call
    //     site (enforced there) before requesting a token, and
    //   • the independent Auth0 shell's own HTTP, where the initial client-list
    //     fetch legitimately has no client yet.
    // So `paramclientid` is optional here — when present it enriches the token;
    // when absent the `/oauth/token` fetch patch backfills it from the active
    // route/storage once a client is selected. Routine calls use SDK default
    // caching; a client switch forces a fresh token via `auth0ForceRefreshNext`.
    if (opts.provider === 'auth0') {
      const forceFresh = auth0ForceRefreshNext;
      auth0ForceRefreshNext = false;
      return getAuth0Token({ paramclientid: opts.paramclientid, forceFresh });
    }

    // `interactive` defaults to true (legacy behaviour): a silent
    // interaction-required failure kicks off the interactive login redirect.
    // The MFE multi-client token path passes `interactive: false` so a scope
    // the primary app can't mint doesn't redirect the whole shell — it just
    // throws so the caller can try the next app registration.
    const interactive = opts.interactive ?? true;
    const account = msalInstance.getActiveAccount();
    if (!account) throw new Error('No active account; user must sign in first.');

    const forceRefresh = forceRefreshNext;
    forceRefreshNext = false;

    const key = cacheKey(account.homeAccountId, scopes);

    if (!forceRefresh) {
      // Fast path: a still-valid cached token (no MSAL lock, no await round-trip).
      const cached = tokenCache.get(key);
      if (cached && cached.expiresAt - Date.now() > EXPIRY_SKEW_MS) {
        return cached.token;
      }
      // Coalesce concurrent misses onto a single in-flight acquisition.
      const pending = inflight.get(key);
      if (pending) return pending;
    }

    const acquisition = (async () => {
      try {
        const result = await msalInstance.acquireTokenSilent({ scopes, account, forceRefresh });
        storeResult(key, result);
        return result.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          clearTokenCache();
          if (interactive) goToLoginInteractive();
        }
        throw err;
      } finally {
        inflight.delete(key);
      }
    })();
    inflight.set(key, acquisition);
    return acquisition;
  },

  getIdToken(): string | null {
    const account = msalInstance.getActiveAccount();
    const idToken = account?.idToken ?? null;
    if (!idToken) return idToken;
    // Legacy parity (`raptor-msal.service.ts:setUserContextSession`): the
    // Angular shell normalizes the ID token's base64url payload segment
    // (`-`/`_`) to standard base64 (`+`/`/`) ONCE, at login, before the token
    // is ever stored or handed to a caller. This shell never did that
    // normalization here, so `EY_RAPToR_getMSALIdToken()` (bound below in
    // `install-globals.ts`) returned the raw MSAL token unchanged.
    //
    // The legacy Angular MFEs' bundled AppInsights logger
    // (`@ey-fso-tax/raptor-appinsightlogger-ui` → `getUserInfo()`) manually
    // `atob()`-decodes this token's payload, assuming it has already been
    // through this exact normalization. Guest/B2B (`eyclients.com`) ID tokens
    // are more likely to retain `-`/`_` bytes in their payload than internal
    // AAD tokens, so without this normalization `atob()` throws
    // `InvalidCharacterError` inside the logger's constructor — which runs
    // during the legacy MFE's Angular `bootstrapModule()`, aborting bootstrap
    // before the MFE's own `customElements.define()` call runs. Symptom:
    // "Custom Element <...-component> was not registered after script
    // injection" from the mfe-loader's `loadViaLegacy` CE-registration
    // timeout — for `eyclients.com` guests specifically.
    const parts = idToken.split('.');
    if (parts.length > 2 && (parts[1].includes('-') || parts[1].includes('_'))) {
      parts[1] = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      return parts.join('.');
    }
    return idToken;
  },

  logout(): void {
    // Legacy `RaptorMsalService.logout()` logs a LOGOUT telemetry event first.
    getTracker().event('Logout');
    // Drop cached tokens so a future session can't observe a stale entry.
    clearTokenCache();
    // Clear per-session caches (RUM token, etc.) registered via onLogout.
    for (const cb of logoutListeners) {
      try {
        cb();
      } catch {
        /* a listener throwing must not block logout */
      }
    }
    // Broadcast logout to all peer tabs so they navigate to the login page,
    // matching Angular shell's `login.service.ts:logOut()` behaviour.
    try {
      const ch = new BroadcastChannel('logout_channel');
      // Independent Auth0 shell (VITE_AUTH_PROVIDER=auth0): Auth0 owns the
      // session, so the SINGLE logout navigation is the Auth0 logout redirect
      // (returns to the configured post-logout URL, i.e. /login) with no MSAL
      // involvement. Clear app storage first, then hand off to Auth0.
      if (shellIsAuth0()) {
        clearAllStorage();
        void logoutAuth0();
        return;
      }
      ch.postMessage({ action: 'logout' });
      ch.close();
    } catch {
      /* BroadcastChannel unavailable in rare embedded contexts */
    }
    // Capture the active account BEFORE clearing storage so MSAL can
    // identify which account to sign out (avoids "Pick an account" prompt).
    const account = msalInstance.getActiveAccount() ?? undefined;
    // Clear the Auth0 SDK's local session too (PRTL-2856). `openUrl: false`
    // (in clearAuth0Session) clears Auth0's cache WITHOUT a second redirect —
    // the shell's primary MSAL `logoutRedirect` below is the single navigation.
    // `clearAllStorage()` then wipes the persisted Auth0 cache + `authProvider`
    // flag + `ey.user.context`/`currentUser`/client-selection keys.
    void clearAuth0Session();
    // Clear all storage so MSAL cached accounts are removed and the next
    // visit lands on the login page instead of auto-redirecting to home.
    // Mirrors Angular `login.service.ts:loggedout()` which calls
    // `localStorage.clear()` + `sessionStorage.clear()`.
    clearAllStorage();
    // Legacy `raptor-msal.service.ts` passes the active account to
    // `logoutRedirect({ account })`, so MSAL signs the current user out
    // directly instead of popping the account picker.
    void msalInstance.logoutRedirect({ account });
  },
};
